import { useQuery } from '@tanstack/react-query';
import { useDataProvider } from 'react-admin';
import {
  buildErasureQueueView,
  type ErasureQueueView,
} from './erasureQueueModel';
import type { ErasureRequest, Market, User } from '../dataProvider/types';

/**
 * The erasure queue, read through the aggregation seam (`buildErasureQueueView`), NOT computed in the
 * component. For v1 it pulls the erasure requests and users from the mock provider and aggregates
 * client-side; the real-data migration replaces the fetch-and-aggregate with a server-side endpoint
 * and the surface does not change. Same seam idiom as useDsarQueue / useSanctionsQueue.
 *
 * OVERSIGHT-GLOBAL: it does NOT read the region switcher. It is global by default; the optional
 * `regionFilter` (the surface's own facet) narrows by the request's market by exact value. The grace
 * derivation is measured against the injected `now`.
 */

const ALL = {
  pagination: { page: 1, perPage: 100_000 },
  sort: { field: 'id', order: 'ASC' as const },
  filter: {},
};

export const useErasureQueue = (
  now: number,
  regionFilter: Market | null,
): { view: ErasureQueueView | null; isLoading: boolean } => {
  const dataProvider = useDataProvider();

  const { data, isLoading } = useQuery({
    queryKey: ['erasure-queue', now, regionFilter],
    queryFn: async () => {
      const [erasureRequests, users] = await Promise.all([
        dataProvider.getList('erasureRequests', ALL),
        dataProvider.getList('users', ALL),
      ]);
      return buildErasureQueueView(
        {
          erasureRequests: erasureRequests.data as unknown as ErasureRequest[],
          users: users.data as unknown as User[],
        },
        { now, regionFilter },
      );
    },
  });

  return { view: data ?? null, isLoading };
};
